// Third Party
import * as React from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import ReactDOM from 'react-dom';
import dynamic from 'next/dynamic';

// Utils
import { isSSR } from '@utils/predicates';
import { useLoginAndSignupModals } from '../../contexts/use-login-and-signup-modals';

// UI
import { IconNames } from '@components/icon';
import { ButtonIcon } from '@components/button-icon';
import { Login as StaticLogin } from '@pages-components/login';
import { useLocation } from 'react-use';

const Login = dynamic<React.ComponentProps<typeof StaticLogin>>(() =>
  import('@pages-components/login').then((mod) => mod.Login)
);

type Props = {};

export const LoginModal: React.FC<Props> = () => {
  const { deactivateLoginModal, loginModalActive } = useLoginAndSignupModals();

  const location = useLocation();
  const redirectUrl =
    typeof window !== 'undefined'
      ? window.location.href || '' + location.search?.toString()
      : '';

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  const closeModal = (): void => {
    deactivateLoginModal();
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (loginModalActive) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [loginModalActive]);

  if (!isSSR) {
    return ReactDOM.createPortal(
      <Transition
        show={loginModalActive}
        className="fixed z-50 inset-0 overflow-y-auto"
        as="div"
        style={{ zIndex: 9000 }}
      >
        <div className="block items-end justify-center sm:px-4 min-h-screen text-center sm:p-0">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 transition-opacity">
              <div className="absolute inset-0 bg-black opacity-60"></div>
            </div>
          </TransitionChild>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="inline-block align-middle h-screen"></span>
          &#8203;
          <TransitionChild
            className="inline-block max-w-xl text-left bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all align-middle sm:w-full lg:w-3/4 border-[1.5px] border-primary-main"
            as="div"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <>
              <div className="flex flex-row mt-4 ml-4">
                <ButtonIcon
                  type="button"
                  onClick={deactivateLoginModal}
                  variant="text"
                  size="sm"
                  icon={IconNames.X}
                  ariaDescribedby="close login modal"
                  className="hover:bg-gray-xxlight px-3 py-[0.6rem]"
                />
              </div>
              <div className="-mt-[2rem] sm:-mt-8 sm:pb-8">
                <Login redirectUrl={redirectUrl} />
              </div>
            </>
          </TransitionChild>
        </div>
      </Transition>,
      document.getElementById('modal-root') as Element
    );
  } else {
    return null;
  }
};
